//
// # Request-Scoped Context
//
// Inspired by Go's context.Context, this provides request-scoped data
// propagation across service boundaries (TypeScript <-> Rust).
//
// Key design decisions:
// - Immutable: `with()` returns a new context, never mutates
// - Lightweight: Just a Map wrapper with typed accessors
// - Cross-service: Serializable to JSON for HTTP propagation
//

// NPM Imports
import { v4 as uuidv4 } from "uuid";

// ============================================================================
// Standard Context Keys
// ============================================================================

/** Unique ID for a logical operation (spans multiple requests) */
export const CTX_LOG_ID = "logId";

/** Unique ID for this specific request */
export const CTX_REQUEST_ID = "requestId";

/** User identifier */
export const CTX_USER = "user";

/** Tenant identifier (for multi-tenant deployments) */
export const CTX_TENANT = "tenant";

/**
 * Per-tab/connection session identifier — uniquely distinguishes one
 * browser-tab session from another, even when the user (email) is the same.
 * Currently sourced from the client-supplied `clientId` field on the request
 * body; the name `clientId` predates this concept and a full rename across
 * browser/server payloads would be a large refactor, so we just alias here.
 */
export const CTX_SESSION_ID = "sessionId";

/** Environment (dev/staging/prod) */
export const CTX_ENV = "env";

/** Request start timestamp (ISO 8601) */
export const CTX_START_TIME = "startTime";

/** Git commit hash of the running code */
export const CTX_COMMIT_HASH = "commitHash";

// ============================================================================
// Context Interface
// ============================================================================

/**
 * Wire format for context serialization (HTTP headers/body).
 * All values are JSON-serializable.
 */
export interface ContextWire {
  logId?: string;
  requestId?: string;
  user?: string;
  tenant?: string;
  env?: string;
  sessionId?: string;
  startTime?: string;
  commitHash?: string;
  [key: string]: unknown;
}

/**
 * Immutable request-scoped context.
 *
 * Usage:
 * ```typescript
 * const ctx = Context.create()
 *   .with(CTX_LOG_ID, "abc123")
 *   .with(CTX_USER, "user@example.com");
 *
 * const logId = ctx.get<string>(CTX_LOG_ID);
 * ```
 */
export class Context {
  private readonly values: Map<string, unknown>;

  private constructor(values?: Map<string, unknown>) {
    this.values = values ? new Map(values) : new Map();
  }

  /**
   * Create an empty context.
   */
  static create(): Context {
    return new Context();
  }

  /**
   * Create a context with standard fields populated.
   * Generates a new requestId and sets startTime to now.
   */
  static background(): Context {
    return new Context()
      .with(CTX_REQUEST_ID, uuidv4())
      .with(CTX_START_TIME, new Date().toISOString());
  }

  /**
   * Create a context from wire format (e.g., from HTTP headers or request body).
   */
  static fromWire(wire: ContextWire): Context {
    const values = new Map<string, unknown>();
    for (const [key, value] of Object.entries(wire)) {
      if (value !== undefined) {
        values.set(key, value);
      }
    }
    return new Context(values);
  }

  /**
   * Get a value from the context.
   * Returns undefined if the key doesn't exist.
   */
  get<T>(key: string): T | undefined {
    return this.values.get(key) as T | undefined;
  }

  /**
   * Check if a key exists in the context.
   */
  has(key: string): boolean {
    return this.values.has(key);
  }

  /**
   * Return a new context with the given key-value pair added.
   * Does NOT mutate this context.
   */
  with(key: string, value: unknown): Context {
    const newValues = new Map(this.values);
    newValues.set(key, value);
    return new Context(newValues);
  }

  /**
   * Return a new context with multiple key-value pairs added.
   * Does NOT mutate this context.
   */
  withMany(entries: Record<string, unknown>): Context {
    const newValues = new Map(this.values);
    for (const [key, value] of Object.entries(entries)) {
      newValues.set(key, value);
    }
    return new Context(newValues);
  }

  /**
   * Serialize to wire format for HTTP propagation.
   */
  toWire(): ContextWire {
    const wire: ContextWire = {};
    for (const [key, value] of this.values) {
      wire[key] = value;
    }
    return wire;
  }

  /**
   * Get all keys in the context.
   */
  keys(): string[] {
    return Array.from(this.values.keys());
  }

  /**
   * Get the number of values in the context.
   */
  get size(): number {
    return this.values.size;
  }

  // ============================================================================
  // Convenience Accessors for Standard Keys
  // ============================================================================

  /** Get the log ID (unique ID for a logical operation) */
  get logId(): string | undefined {
    return this.get<string>(CTX_LOG_ID);
  }

  /** Get the request ID (unique ID for this specific request) */
  get requestId(): string | undefined {
    return this.get<string>(CTX_REQUEST_ID);
  }

  /** Get the user identifier */
  get user(): string | undefined {
    return this.get<string>(CTX_USER);
  }

  /** Get the tenant identifier */
  get tenant(): string | undefined {
    return this.get<string>(CTX_TENANT);
  }

  /** Get the environment */
  get env(): string | undefined {
    return this.get<string>(CTX_ENV);
  }

  /** Get the commit hash */
  get commitHash(): string | undefined {
    return this.get<string>(CTX_COMMIT_HASH);
  }

  /** Get the start time */
  get startTime(): string | undefined {
    return this.get<string>(CTX_START_TIME);
  }

  /**
   * Calculate elapsed time in milliseconds since startTime.
   * Returns undefined if startTime is not set.
   */
  elapsedMs(): number | undefined {
    const start = this.startTime;
    if (!start) return undefined;
    const startDate = new Date(start);
    return Date.now() - startDate.getTime();
  }
}

// ============================================================================
// Context Factory Functions
// ============================================================================

/**
 * Options for creating a context.
 */
export interface ContextOptions {
  /** Environment identifier (e.g., "vscode-extension", "alpha-server") */
  env: string;
  /** Tenant identifier */
  tenant?: string;
  /** Git commit hash of the running code */
  commitHash?: string;
  /** User identifier (optional - caller can provide if available) */
  user?: string;
  /** Per-tab session identifier (optional) */
  sessionId?: string;
}

/**
 * Generate a unique log ID for tracing.
 * Format: {YYYYMMDDHHMMSS}{hex} for chronological sorting and uniqueness.
 * Uses UUID internally for the random component (browser-compatible).
 */
export function generateLogId(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");
  const seconds = String(now.getSeconds()).padStart(2, "0");
  const timestamp = `${year}${month}${day}${hours}${minutes}${seconds}`;

  // Use UUID and take first 16 chars (browser-compatible alternative to crypto.randomBytes)
  const hex = uuidv4().replace(/-/g, "").substring(0, 16);

  return `${timestamp}${hex}`;
}

/**
 * Create a new Context for a user-initiated request.
 * Populates standard context values like environment, tenant, and log ID.
 *
 * @param opts - Environment-specific options (env, tenant, commitHash, user)
 */
export function createRequestContext(opts: ContextOptions): Context {
  // Start with background context (auto-generates requestId and startTime)
  let ctx = Context.background();

  // Add log ID for correlating logs across services
  ctx = ctx.with(CTX_LOG_ID, generateLogId());

  // Add user identity if provided
  if (opts.user) {
    ctx = ctx.with(CTX_USER, opts.user);
  }

  // Add tenant
  if (opts.tenant) {
    ctx = ctx.with(CTX_TENANT, opts.tenant);
  }

  // Add environment indicator
  ctx = ctx.with(CTX_ENV, opts.env);

  // Add session identifier (per browser-tab) if provided
  if (opts.sessionId) {
    ctx = ctx.with(CTX_SESSION_ID, opts.sessionId);
  }

  // Add commit hash for version tracking
  if (opts.commitHash) {
    ctx = ctx.with(CTX_COMMIT_HASH, opts.commitHash);
  }

  return ctx;
}

/**
 * Create a Context for internal/background operations.
 * Use this when there's no user request to propagate context from.
 *
 * @param opts - Environment-specific options (env, tenant, commitHash)
 */
export function createBackgroundContext(opts: ContextOptions): Context {
  let ctx = Context.background();
  ctx = ctx.with(CTX_LOG_ID, generateLogId());

  if (opts.tenant) {
    ctx = ctx.with(CTX_TENANT, opts.tenant);
  }

  ctx = ctx.with(CTX_ENV, opts.env);

  if (opts.commitHash) {
    ctx = ctx.with(CTX_COMMIT_HASH, opts.commitHash);
  }

  return ctx;
}
